import { watch, type Ref } from 'vue';
import type { Passenger } from '@aviata/booking-data';
import { controllerModel } from 'shared';

import type { accountModel } from '@/entities/account';

/**
 * Auto-redirect away from an empty saved-passengers sheet. When the
 * sheet opens but the filtered list resolves to zero candidates, hide
 * it and trigger the add-new-passenger flow instead. Uses
 * `flush: 'post'` and a "account resolved" guard so the redirect waits
 * past the gap between `isLoading` flipping to false and the saved
 * passengers actually populating into `visibleSavedPassengers` — without
 * that guard we'd briefly observe an empty list and pop the wrong flow.
 *
 * `accountData` is passed in rather than called internally so the caller
 * can share the same query observer it already holds.
 */
export const useSkipEmptySavedList = (options: {
  savedSheetName: string;
  accountData: ReturnType<typeof accountModel.useAccount>;
  visibleSavedPassengers: Ref<Passenger[]>;
  onAddNewPassenger: () => void;
}) => {
  const bottomSheet = controllerModel.useBottomSheet();
  const { accountData } = options;

  watch(
    [
      () => bottomSheet.isVisible(options.savedSheetName),
      () => accountData.isLoading.value,
      () => accountData.data.value,
      options.visibleSavedPassengers,
    ],
    ([isSavedSheetVisible, isAccountLoading, accountResult, filtered]) => {
      if (isSavedSheetVisible !== true || isAccountLoading) {
        return;
      }
      const accountResolved =
        accountResult !== undefined || accountData.isError.value;
      if (!accountResolved || filtered.length > 0) {
        return;
      }
      bottomSheet.hide(options.savedSheetName);
      options.onAddNewPassenger();
    },
    { flush: 'post' },
  );
};
